import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { EnvelopeIcon, LockClosedIcon, ArrowRightIcon, KeyIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { base64urlToUint8Array, uint8ArrayToBase64url } from '../../utils/webauthn';
import LanguageSelector from '../../components/LanguageSelector';
import OTPInput from '../../components/Auth/OTPInput';

interface LoginFormData {
  email: string;
  password: string;
}

const LOGIN_OTP_PENDING_KEY = 'login_otp_pending';

const ResendButton: React.FC<{ email: string; disabled: boolean }> = ({ email, disabled }) => {
  const { t } = useTranslation('login');
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;
    try {
      setSending(true);
      await apiService.resendVerificationOtp(email);
      toast.success(t('otp.resendSuccess'));
      setCooldown(30);
      const interval = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch {
      toast.error(t('messages.failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleResend}
      disabled={disabled || sending || cooldown > 0}
      className="text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {cooldown > 0 ? t('otp.resendIn', { count: cooldown }) : t('otp.resend')}
    </button>
  );
};

const Login: React.FC = () => {
  const { t } = useTranslation('login');
  const { login, isLoading, checkAuthStatus } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get('email') || '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  // When login fails with "verify your email", show OTP form so user can enter existing code (no new email sent)
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  const [otpError, setOtpError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Restore "show OTP form" from sessionStorage after remount (AuthContext sets isLoading=false and App swaps to Routes, remounting Login)
  // NOTE: only the email is persisted — the password is intentionally NOT stored in sessionStorage.
  // If the component remounts, pendingPassword will be empty and onOtpComplete will redirect back to
  // the login form after successful verification so the user re-enters their password.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(LOGIN_OTP_PENDING_KEY);
      if (raw) {
        const data = JSON.parse(raw) as { email: string };
        if (data?.email) {
          setPendingEmail(data.email);
          setShowOtpForm(true);
        }
        sessionStorage.removeItem(LOGIN_OTP_PENDING_KEY);
      }
    } catch {
      sessionStorage.removeItem(LOGIN_OTP_PENDING_KEY);
    }
  }, []);

  // Surface a "post-logout" error message that the API interceptor stashed in
  // localStorage when an authenticated request hit a hard refusal (e.g. 402
  // SUBSCRIPTION_EXPIRED from LicenseValidityMiddleware). Without this, the
  // forced bounce to /login would leave the user staring at an empty form
  // with no clue why they're logged out. We toast it once and clear the keys.
  useEffect(() => {
    try {
      const msg = localStorage.getItem('postLogoutErrorMessage');
      const reason = localStorage.getItem('postLogoutErrorReason');
      if (msg) {
        toast.error(msg, { id: `post-logout-${reason || 'unknown'}`, duration: 8000 });
      }
    } catch {
      // ignore
    } finally {
      try {
        localStorage.removeItem('postLogoutErrorMessage');
        localStorage.removeItem('postLogoutErrorReason');
      } catch {
        // ignore
      }
    }
  }, []);

  // Check if WebAuthn is available
  useEffect(() => {
    setPasskeyAvailable(
      typeof window !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      typeof navigator.credentials !== 'undefined' &&
      typeof navigator.credentials.get === 'function'
    );
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { email: emailFromQuery } });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsSubmitting(true);
      setShowOtpForm(false);
      setOtpError('');
      await login(data.email, data.password);
      toast.success(t('messages.success'));
      navigate('/');
    } catch (error: any) {
      const dataBody = error?.response?.data;
      const status = error?.response?.status;
      const requestUrl = error?.config?.url ?? '';
      const isLoginRequest = String(requestUrl).includes('login');
      const code = dataBody?.code;
      const isEmailNotVerified = code === 'EMAIL_NOT_VERIFIED' || (error as any)?.emailNotVerified === true;

      // Check if password is expired
      if (code === 'PASSWORD_EXPIRED' || dataBody?.requires_reset) {
        toast.error(t('messages.passwordExpired'));
        navigate(`/forgot-password?email=${encodeURIComponent(data.email)}&expired=true`);
        return;
      }

      // Backend returns code "EMAIL_NOT_VERIFIED" for unverified users — show OTP form (no new email sent)
      // Persist to sessionStorage so remounted Login (App shows spinner then Routes again) can restore OTP form
      if (isEmailNotVerified) {
        try {
          sessionStorage.setItem(LOGIN_OTP_PENDING_KEY, JSON.stringify({ email: data.email }));
        } catch {
          // ignore
        }
        setPendingEmail(data.email);

        setOtpError('');
        setTimeout(() => setShowOtpForm(true), 0);
        return;
      }

      const is403 = status === 403;
      const errorMessage =
        (typeof dataBody === 'object' && dataBody != null && typeof dataBody.error === 'string' && dataBody.error) ||
        (typeof dataBody === 'object' && dataBody != null && typeof dataBody.message === 'string' && dataBody.message) ||
        (typeof dataBody === 'string' && dataBody) ||
        error.message ||
        t('messages.failed');
      const messageStr = String(errorMessage).toLowerCase();
      const suggestsVerify =
        messageStr.includes('verify your email') || messageStr.includes('verify your email before signing in');
      const bodyString =
        typeof dataBody === 'string' ? dataBody : dataBody != null ? JSON.stringify(dataBody) : '';
      const bodySuggestsVerify = bodyString.toLowerCase().includes('verify');
      const is403OnLoginWithNoBody = is403 && isLoginRequest && (dataBody == null || bodyString === '{}');

      // Fallback: 403 on login with verify message in body, or 403 on login with no/empty body (proxy may strip it)
      if (is403 && isLoginRequest && (suggestsVerify || bodySuggestsVerify || is403OnLoginWithNoBody)) {
        try {
          sessionStorage.setItem(LOGIN_OTP_PENDING_KEY, JSON.stringify({ email: data.email }));
        } catch {
          // ignore
        }
        setPendingEmail(data.email);

        setOtpError('');
        setTimeout(() => setShowOtpForm(true), 0);
        return;
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onOtpComplete = async (enteredOtp: string) => {
    if (!pendingEmail) return;
    try {
      setIsVerifying(true);
      setOtpError('');
      await apiService.verifyEmail(pendingEmail, enteredOtp);
      sessionStorage.removeItem(LOGIN_OTP_PENDING_KEY);
      // verifyEmail already sets auth cookies server-side. Hard-navigate so the app
      // bootstraps fresh and picks up the new session without any React state race.
      window.location.replace('/');
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || t('messages.failed');
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setIsVerifying(false);
    }
  };

  // Passkey login with email lookup
  const handlePasskeyLoginWithEmail = async () => {
    if (!userEmail) {
      toast.error(t('messages.emailRequired'));
      return;
    }

    try {
      setIsPasskeyLoading(true);
      
      // First, check if passkey login is enabled for this user
      // We'll do this by attempting to begin authentication - the backend should check
      // But we also want to show a helpful message if login is not enabled
      // For now, we'll proceed and let the backend handle the check
      // If the backend returns an error about login not being enabled, we'll show it
      
      const beginResponse = await apiService.webauthnBeginAuthentication(undefined, userEmail);
      const { options, session_id } = beginResponse;
      
      // If we get here, the backend has allowed the authentication to begin
      // But we should still check if login is enabled in passkey_config
      // Since we can't get preferences without auth, we'll rely on the backend check

      // Use browser WebAuthn API
      const challengeBytes = base64urlToUint8Array(options.publicKey.challenge) as unknown as Uint8Array;
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: challengeBytes.buffer as ArrayBuffer,
          allowCredentials: options.publicKey.allowCredentials?.map((cred: any) => {
            const idBytes = base64urlToUint8Array(cred.id) as unknown as Uint8Array;
            return {
              id: idBytes.buffer as ArrayBuffer,
              type: cred.type,
              transports: cred.transports,
            };
          }),
          timeout: options.publicKey.timeout || 60000,
          userVerification: options.publicKey.userVerification || 'preferred',
        },
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error(t('messages.passkeyCancelled'));
      }

      // Convert response to WebAuthn JSON format (base64url-encoded strings)
      const response = credential.response as AuthenticatorAssertionResponse;
      const assertionResponse = {
        id: credential.id,
        rawId: uint8ArrayToBase64url(new Uint8Array(credential.rawId)),
        response: {
          authenticatorData: uint8ArrayToBase64url(new Uint8Array(response.authenticatorData)),
          clientDataJSON: uint8ArrayToBase64url(new Uint8Array(response.clientDataJSON)),
          signature: uint8ArrayToBase64url(new Uint8Array(response.signature)),
          userHandle: response.userHandle ? uint8ArrayToBase64url(new Uint8Array(response.userHandle)) : null,
        },
        type: credential.type,
      };

      // Finish authentication
      const finishResponse = await apiService.webauthnFinishAuthentication(session_id, assertionResponse);

      if (!finishResponse.success) {
        throw new Error(finishResponse.message || 'Passkey authentication failed');
      }

      // Login successful - refresh auth status
      await checkAuthStatus();
      toast.success(t('messages.successPasskey'));
      navigate('/');
    } catch (error: any) {
      console.error('Passkey login error:', error);
      let errorMessage = t('messages.passkeyFailed');
      if (error.name === 'NotAllowedError') {
        errorMessage = t('messages.passkeyCancelledError');
      } else if (error.name === 'InvalidStateError') {
        errorMessage = t('messages.passkeyInvalidError');
      } else if (error.name === 'NotSupportedError') {
        errorMessage = t('messages.passkeyNotSupported');
      } else if (error.response?.data?.error) {
        // Use the error message from backend (which will include the passkey login not enabled message)
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12 relative">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-50">
        <LanguageSelector />
      </div>
      
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="mb-8">
          <div className="flex items-start mb-6">
            <img 
              src="/logo-gov.svg" 
              alt="Governate Logo" 
              className="h-16"
            />
          </div>
          {!showOtpForm && <><h1 className="text-3xl font-bold text-white mb-2">{t('title')}</h1>
          <p className="text-gray-400">{t('subtitle')}</p></>}
        </div>

        {/* OTP form when login rejected due to unverified email */}
        {showOtpForm ? (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 md:p-8 shadow-2xl">
            <button
              type="button"
              onClick={() => { sessionStorage.removeItem(LOGIN_OTP_PENDING_KEY); setShowOtpForm(false); setOtpError(''); }}
              className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-6"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              {t('otp.backToSignIn')}
            </button>
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white mb-2">{t('otp.title')}</h2>
              <p className="text-gray-400 text-sm mb-2">
                {t('otp.requiredMessage')}
              </p>
              <p className="text-gray-400 text-sm">
                {t('otp.description')} <span className="font-semibold text-white">{pendingEmail}</span>
              </p>
            </div>
            <div className="space-y-4">
              <OTPInput
                onComplete={onOtpComplete}
                disabled={isVerifying}
                error={otpError}
                variant="dark"
              />
              <div className="flex justify-center">
                <ResendButton email={pendingEmail} disabled={isVerifying} />
              </div>
            </div>
          </div>
        ) : (
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 shadow-2xl">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {t('form.email.label')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  {...register('email', {
                    required: t('form.email.required'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('form.email.invalid'),
                    },
                  })}
                  type="email"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                  placeholder={t('form.email.placeholder')}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                {t('form.password.label')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-500" />
                </div>
                <input
                  {...register('password', {
                    required: t('form.password.required'),
                    minLength: {
                      value: 6,
                      message: t('form.password.minLength'),
                    },
                  })}
                  type="password"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                  placeholder={t('form.password.placeholder')}
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <button 
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-gray-400 hover:text-white font-medium transition-colors"
              >
                {t('form.forgotPassword')}
              </button>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="w-full bg-transparent border-2 border-white text-white py-3 px-4 rounded-xl font-medium hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center group"
            >
              {isSubmitting || isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('form.submitting')}
                </>
              ) : (
                <>
                  {t('form.submit')}
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          {passkeyAvailable && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white/5 text-gray-400">{t('passkey.divider')}</span>
                </div>
              </div>

              {/* Passkey Login Button */}
              <button
                type="button"
                onClick={handlePasskeyLoginWithEmail}
                disabled={isPasskeyLoading || isLoading || !userEmail}
                className="mt-6 w-full bg-white/10 border border-white/20 text-white py-3 px-4 rounded-xl font-medium hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center group"
              >
                {isPasskeyLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t('passkey.authenticating')}
                  </>
                ) : (
                  <>
                    <KeyIcon className="w-5 h-5 mr-2" />
                    {t('passkey.button')}
                  </>
                )}
              </button>
              <p className="mt-2 text-xs text-center text-gray-500">
                {t('passkey.hint')}
              </p>
            </div>
          )}
        </div>
        )}

        <p className="text-center text-sm text-gray-600 mt-6">
          {t('noAccount')}{' '}
          <Link to="/register" className="text-white hover:underline font-medium">
            {t('registerLink')}
          </Link>
        </p>
        <p className="text-center text-sm text-gray-600 mt-2">
          {t('copyright')}
        </p>
      </div>
    </div>
  );
};

export default Login;
